import type { RoadSection } from '../domain/Reference';
import { attributeBreakdowns, MULTI_ATTRIBUTE_FIELDS, MultiAttributeField } from './selection';

// Rules of the referential edit dialog (see docs/map-v2/plan.md, Phase 2). Pure, so they are tested apart
// from the dialog.

export type EditableField = MultiAttributeField;
export const EDITABLE_FIELDS: readonly EditableField[] = MULTI_ATTRIBUTE_FIELDS;

export type EditValue = string | number | null;

export type FieldKind = 'list' | 'year' | 'decimal';

export const FIELD_KINDS: Record<EditableField, FieldKind> = {
    category: 'list',
    gestionary: 'list',
    roadType: 'list',
    trucks: 'list',
    urban: 'list',
    year: 'year',
    width: 'decimal',
};

// Always offered for the field, on top of the values already in the organization's data.
const SEED_OPTIONS: Partial<Record<EditableField, string[]>> = { urban: ['oui', 'non'] };

// The backend rejects years outside this range (UpdateRoadSectionsUseCase).
export const MIN_YEAR = 1900;

const textOf = (value: string | number | null): string | null => {
    if (value === null) {
        return null;
    }
    const text = String(value).trim();

    return text === '' ? null : text;
};

// The values already used in the organization for a text field, the most used first, plus the field's seeds.
// The columns are free text: this is what keeps a dropdown consistent without imposing a list.
export const listOptions = (sections: RoadSection[], field: EditableField): string[] => {
    const counts = new Map<string, number>();
    sections.forEach((section) => {
        const value = textOf(section[field]);
        if (value !== null) {
            counts.set(value, (counts.get(value) ?? 0) + 1);
        }
    });
    const used = Array.from(counts.entries())
        .sort(([nameA, countA], [nameB, countB]) => countB - countA || nameA.localeCompare(nameB, 'fr'))
        .map(([value]) => value);
    const seeds = (SEED_OPTIONS[field] ?? []).filter((seed) => !counts.has(seed));

    return [...used, ...seeds];
};

// What the input starts with: the shared value when the selection agrees on one, otherwise empty.
export const initialInput = (selected: RoadSection[], field: EditableField): string => {
    const breakdown = attributeBreakdowns(selected).find((entry) => entry.field === field);
    if (!breakdown?.homogeneous) {
        return '';
    }
    const first = selected[0];

    return first ? textOf(first[field]) ?? '' : '';
};

export type ParsedEdit = { ok: true; value: EditValue } | { ok: false; error: 'year' | 'width' };

// Turns what was typed into the value to save. Empty clears the field (null). Years are whole and within the
// range the backend accepts; widths are non-negative numbers, with a decimal comma allowed.
export const parseEditInput = (
    field: EditableField,
    input: string,
    currentYear: number = new Date().getFullYear(),
): ParsedEdit => {
    const text = input.trim();
    if (text === '') {
        return { ok: true, value: null };
    }
    const kind = FIELD_KINDS[field];
    if (kind === 'year') {
        const year = Number(text);
        if (!/^\d{4}$/.test(text) || year < MIN_YEAR || year > currentYear) {
            return { ok: false, error: 'year' };
        }

        return { ok: true, value: year };
    }
    if (kind === 'decimal') {
        const width = Number(text.replace(',', '.'));
        if (!Number.isFinite(width) || width < 0) {
            return { ok: false, error: 'width' };
        }

        return { ok: true, value: width };
    }

    return { ok: true, value: text };
};

// How many of the selected sections would actually change, to tell the user what the save does.
export const changedCount = (selected: RoadSection[], field: EditableField, value: EditValue): number =>
    selected.filter((section) => {
        const current = section[field];

        return (current === '' ? null : current) !== value;
    }).length;

export interface EditNotice {
    field: EditableField;
    count: number;
}

export type EditResult = { status: 'ok' } | { status: 'invalid'; field: string } | { status: 'error' };
